import useConditionalEffect from 'hooks/useConditionalEffect'
import {
  AnyContent,
  Article,
  ARTICLE_TYPE,
  CuratedFolder,
  Photo,
  PHOTO_TYPE,
  STATUS_TYPE,
  StatusIdea,
  Video,
  VIDEO_TYPE
} from 'interfaces'
import { FC, useCallback, useRef } from 'react'
import { connect } from 'react-redux'
import { animationFrameScheduler, EMPTY, Observable, of as observableOf, Subscription } from 'rxjs'
import { mergeAll, mergeMap, observeOn, toArray } from 'rxjs/operators'
import { getArticles } from 'services/content/articles/actions'
import { getPhotos } from 'services/content/photos/actions'
import { getStatusIdeas } from 'services/content/statuses/actions'
import { getVideos } from 'services/content/videos/actions'
import { StoreState, StoreThunkDispatch } from 'store/state'
import { createHandledPipe } from './FetchError'
import { ContentType } from 'shared'

export interface FetchCuratedFolderContentProps {
  folder: CuratedFolder
  hash?: string
  statusesSeed?: string

  onFetched(content: AnyContent[]): void
  onFailed?(err: any): void
}

interface ConnectedFetchCuratedFolderContentProps extends FetchCuratedFolderContentProps {
  getVideos(source: string): Observable<Video>
  getPhotos(source: string): Observable<Photo>
  getArticles(source: string): Observable<Article>
  getStatusIdeas(source: string[]): Observable<StatusIdea>
}

export const FetchCuratedFolderContent: FC<ConnectedFetchCuratedFolderContentProps> = ({
  folder,
  hash,
  statusesSeed,
  onFetched,
  onFailed,
  getVideos: getVideosFn,
  getPhotos: getPhotosFn,
  getArticles: getArticlesFn,
  getStatusIdeas: getStatusIdeasFn
}) => {
  const subscriptionRef = useRef<Subscription | null>(null)

  const handleFailed = useCallback((err: any) => {
    console.log(err)
    if (onFailed) {
      onFailed(err)
    }
  }, [onFailed])

  const fetch = useCallback(() => {
    const grouped = folder.streams.reduce((result: { [key: string]: string[] }, source: any) => {
      source.types.forEach((type: ContentType) => {
        result[type] = result[type] || []
        result[type].push(source.id + '')
      })
      return result
    }, {} as { [key: string]: string[] })

    const requests = [] as Observable<AnyContent>[]

    if (grouped[VIDEO_TYPE] && grouped[VIDEO_TYPE].length) {
      requests.push(
        createHandledPipe(
          VIDEO_TYPE,
          observableOf(...(grouped[VIDEO_TYPE]).map(getVideosFn)).pipe(mergeAll()),
          handleFailed
        )
      )
    }

    if (grouped[PHOTO_TYPE] && grouped[PHOTO_TYPE].length) {
      requests.push(
        createHandledPipe(
          PHOTO_TYPE,
          observableOf(...(grouped[PHOTO_TYPE]).map(getPhotosFn)).pipe(mergeAll()),
          handleFailed
        )
      )
    }

    if (grouped[ARTICLE_TYPE] && grouped[ARTICLE_TYPE].length) {
      requests.push(
        createHandledPipe(
          ARTICLE_TYPE,
          observableOf(...(grouped[ARTICLE_TYPE]).map(getArticlesFn)).pipe(mergeAll()),
          handleFailed
        )
      )
    }

    if (grouped[STATUS_TYPE] && grouped[STATUS_TYPE].length) {
      requests.push(
        createHandledPipe(
          STATUS_TYPE,
          getStatusIdeasFn(grouped[STATUS_TYPE]),
          handleFailed
        )
      )
    }

    return observableOf(...requests)
      .pipe(
        mergeAll(),
        toArray(),
        observeOn(animationFrameScheduler)
      )
      .subscribe({ next: onFetched, error: handleFailed })
  }, [
    folder.streams,
    getVideosFn,
    getPhotosFn,
    getArticlesFn,
    getStatusIdeasFn,
    handleFailed,
    onFetched
  ])

  useConditionalEffect(([_fetch]) => {
    subscriptionRef.current = _fetch()

    return () => {
      subscriptionRef.current?.unsubscribe()
    }
  }, [hash, statusesSeed], [fetch])

  return null
}

function mapStateToProps(_state: StoreState, ownProps: FetchCuratedFolderContentProps) {
  return ownProps
}

function mapDispatchToProps(dispatch: StoreThunkDispatch, ownProps: FetchCuratedFolderContentProps) {
  const { statusesSeed } = ownProps

  return {
    getVideos: (feedId: string) => dispatch(getVideos(feedId, true, undefined, undefined)).pipe(
      mergeMap(v => observableOf(...v.items))),
    getPhotos: (feedId: string) => dispatch(getPhotos(feedId, true, undefined, undefined)).pipe(
      mergeMap(p => observableOf(...p.items))),
    getArticles: (feedId: string) => dispatch(getArticles(feedId, true, undefined, undefined)).pipe(
      mergeMap(a => observableOf(...a.items))),
    getStatusIdeas: (categoryIds: Array<string>) => {
      if (categoryIds.length === 0) {
        return EMPTY
      }
      return dispatch(getStatusIdeas(categoryIds, undefined, statusesSeed)).pipe(mergeMap(s => observableOf(...s.items)))
    }
  }
}

export default connect(mapStateToProps, mapDispatchToProps)(FetchCuratedFolderContent)
